import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import Select from '../../../../../../../../../components/common/select';
import Icon from '../../../../../../../../../components/common/icon';
import { useDidMount } from '../../../../../../../../../components/common/effects';
import {
  Visibility,
  Option,
} from '../../../../../../../../../types/common-types';
import { bbCodeToHtml } from '../../../../../../../../../utils/formUtils';
import './style.scss';

interface CalculatorValue {
  amount: string;
  installments: string;
  firstRepaymentDate: string;
}
interface Labels {
  header: string;
  selectLoanAmount: string;
  selectLoanInstallments: string;
  title: string;
  titleInfo: string;
  selectFirstRepaymentDate: string;
  repaymentComment: string;
  loanInfoTitle: string;
  loanAmountSelected: string;
  interestExpected: string;
  feesExpected: string;
  instlammentsSelected: string;
  firstRepaymentDateSelected: string;
  totalAmount: string;
  loanInfo: string;
  loanInfoSecondLine: string
}
interface Settings {
  interestRate: number;
  amount: { min: number; max: number; step: number };
  installments: { min: number; max: number; step: number };
  options: Option[];
}

interface Props {
  id: string;
  visibility?: Visibility;
  value: CalculatorValue;
  example: {
    installmentAmount: string;
    interestExpected: string;
    feesExpected: string;
    totalAmount: string;
  };
  settings: Settings;
  extra: {
    labels: Labels;
  };
  onSubmit: (id: string) => void;
  onChange: (
    value: {
      id: string;
      value: object;
      error: boolean;
    },
    callback?: () => void
  ) => void;
}

const Calculator: React.FC<Props> = ({
  id,
  visibility,
  extra,
  value,
  settings,
  example,
  onChange,
  onSubmit,
}) => {
  const { labels } = extra;
  const {
    header,
    title,
    titleInfo,
    selectFirstRepaymentDate,
    selectLoanAmount,
    selectLoanInstallments,
    repaymentComment,
    loanInfoTitle,
    loanAmountSelected,
    interestExpected,
    feesExpected,
    instlammentsSelected,
    firstRepaymentDateSelected,
    totalAmount,
    loanInfo,
    loanInfoSecondLine
  } = labels;
  const {
    installments: settingsInstallments,
    amount: settingsAmount,
    options,
  } = settings;
  const { amount, installments, firstRepaymentDate } = value;
  const didMount = useDidMount();
  const [showInfo, setShowInfo] = useState(true);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (didMount) onSubmit(id);
    }, 50);
    return () => clearTimeout(timeout);
  }, [value]);

  const handleChange = (key: string, val: string) => {
    const newData = { ...value, [key]: val };
    onChange({ id, value: newData, error: false });
  };

  const loanInfoReplaced = loanInfo.replace('{{amount}}', amount);

  return (
    <div
      id={id}
      data-cy="calculator"
      styleName={cn('calculator', {
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calculator__header">
        <span>{header}</span>
      </div>
      <div styleName="calculator__divider" />
      <div styleName="calculator__editor">
        <div styleName="calculator__editor__item">
          <div styleName="calculator__editor__header">
            <span>{selectLoanAmount}</span>
            <span styleName="calculator__editor__value">
              <b>{`${amount}`}</b>
              &euro;
            </span>
          </div>
          <div styleName="calculator__editor__slider">
            <input
              type="range"
              data-cy-prop="value.amount"
              min={settingsAmount.min}
              max={settingsAmount.max}
              step={settingsAmount.step}
              value={amount}
              onChange={e => handleChange('amount', e.target.value)}
            />
          </div>
          <div styleName="calculator__editor__footer">
            <span>
              {`${settingsAmount.min}`}
              &euro;
            </span>
            <span>
              {`${settingsAmount.max}`}
              &euro;
            </span>
          </div>
        </div>
        <div styleName="calculator__editor__item">
          <div styleName="calculator__editor__header">
            <span>{selectLoanInstallments}</span>
            <span styleName="calculator__editor__value">
              <b>{`${installments}`}</b>
            </span>
          </div>
          {settingsInstallments.max - settingsInstallments.min > 0 ? (
            <>
              <div styleName="calculator__editor__slider">
                <input
                  type="range"
                  data-cy-prop="value.duration"
                  min={settingsInstallments.min}
                  max={settingsInstallments.max}
                  step={settingsInstallments.step}
                  value={installments}
                  onChange={e => handleChange('installments', e.target.value)}
                />
              </div>
              <div styleName="calculator__editor__footer">
                <span>{`${settingsInstallments.min}`}</span>
                <span>{`${Math.round(settingsInstallments.max / 2)}`}</span>
                <span>{`${settingsInstallments.max}`}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <div styleName="calculator__title">
        <span>
          {`${title} `}
          <b>
            {`${example.installmentAmount}`}
            &euro;
          </b>
        </span>
      </div>
      <div styleName="calculator__label">
        <span>{`${titleInfo} `}</span>
      </div>
      <Select
        id={id}
        styleName="calculator__select"
        label={selectFirstRepaymentDate}
        visibility={visibility}
        value={firstRepaymentDate}
        options={options}
        onChange={e => handleChange('firstRepaymentDate', e.value)}
      />
      <div styleName="calculator__label">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: bbCodeToHtml(repaymentComment),
          }}
        />
      </div>
      <div
        styleName="calculator__dropdown"
        role="button"
        tabIndex={0}
        onKeyPress={() => {
          setShowInfo(!showInfo);
        }}
        onClick={() => {
          setShowInfo(!showInfo);
        }}
      >
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: bbCodeToHtml(loanInfoTitle),
          }}
        />
        <div styleName="calculator__dropdown__icon">
          <Icon value="dropdown" />
        </div>
      </div>
      {showInfo ? (
        <div styleName="calculator__summary">
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">{loanAmountSelected}</span>
            <span styleName="calculator__row__val bold">
              {`${amount}`}
              &euro;
            </span>
          </div>
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">{interestExpected}</span>
            <span styleName="calculator__row__val bold">
              {`${example.interestExpected}`}
              &euro;
            </span>
          </div>
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">{feesExpected}</span>
            <span styleName="calculator__row__val bold">
              {`${example.feesExpected}`}
              &euro;
            </span>
          </div>
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">{instlammentsSelected}</span>
            <span styleName="calculator__row__val bold">
              {`${installments} cuotas ${example.installmentAmount}`}
              &euro;
            </span>
          </div>
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">
              {firstRepaymentDateSelected}
            </span>
            <span styleName="calculator__row__val bold">
              {`${firstRepaymentDate}`}
            </span>
          </div>
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">{totalAmount}</span>
            <span styleName="calculator__row__val bold">
              {`${example.totalAmount}`}
              &euro;
            </span>
          </div>
        </div>
      ) : null}
      <div styleName="calculator__label small">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: bbCodeToHtml(loanInfoReplaced),
          }}
        />
      </div>
      <div styleName="calculator__label small">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: bbCodeToHtml(loanInfoSecondLine),
          }}
        />
      </div>
    </div>
  );
};

export default Calculator;
